import React, { useState, useEffect } from 'react';
import { Plus, Target, Trash2, Edit3, ChevronRight, CheckCircle, Clock, PauseCircle } from 'lucide-react';
import { useInvestmentContext } from '../context/InvestmentContext';
import GoalCard from '../components/GoalCard';
import GoalForm from '../components/GoalForm';
import { formatCurrency, formatPercent } from '../utils/formatters';

const STATUS_ICONS = {
  active: <Clock className="w-4 h-4 text-blue-500" />,
  completed: <CheckCircle className="w-4 h-4 text-green-500" />,
  paused: <PauseCircle className="w-4 h-4 text-yellow-500" />,
  cancelled: <Trash2 className="w-4 h-4 text-red-400" />,
};

const STATUS_LABELS = {
  active: 'Active',
  completed: 'Completed',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

const GoalsScreen = () => {
  const { goals, goalsLoading: loading, error, fetchGoals, createGoal, updateGoal, deleteGoal, clearError } = useInvestmentContext();
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchGoals(filter !== 'all' ? { status: filter } : {});
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (data) => {
    await createGoal(data);
    setShowForm(false);
  };

  const handleUpdate = async (data) => {
    if (!editingGoal) return;
    await updateGoal(editingGoal.id, data);
    setEditingGoal(null);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    await deleteGoal(id);
    setConfirmDelete(null);
  };

  const handleMarkComplete = async (id) => {
    await updateGoal(id, { status: 'completed' });
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setShowForm(true);
  };

  const filteredGoals = filter === 'all' ? goals : goals.filter((g) => g.status === filter);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Investment Goals</h1>
            <p className="text-gray-500 text-sm">{goals.length} goal{goals.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { setEditingGoal(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New Goal
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="max-w-4xl mx-auto mt-4 flex gap-2 overflow-x-auto pb-1">
          {['all', 'active', 'completed', 'paused'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? 'All' : STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex justify-between">
            <span>{error}</span>
            <button onClick={clearError} className="font-medium">Dismiss</button>
          </div>
        )}

        {loading && goals.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : filteredGoals.length === 0 ? (
          <div className="text-center py-16">
            <Target className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No goals found</p>
            <p className="text-gray-400 text-sm mt-1">Create your first investment goal to get started</p>
            <button
              onClick={() => { setEditingGoal(null); setShowForm(true); }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
            >
              Create Goal
            </button>
          </div>
        ) : (
          filteredGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onEdit={() => handleEdit(goal)}
              onDelete={() => setConfirmDelete(goal.id)}
              onMarkComplete={() => handleMarkComplete(goal.id)}
            />
          ))
        )}
      </div>

      {/* Goal Form Modal */}
      {showForm && (
        <GoalForm
          goal={editingGoal}
          onSubmit={editingGoal ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditingGoal(null); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Goal?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently delete the goal and all associated data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalsScreen;
